"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const strings_1 = require("../../../constants/strings");
const uiConstants_1 = require("../../../constants/uiConstants");
const conversationQuery_1 = require("../../../queries/conversationQuery");
const userQuery_1 = require("../../../queries/userQuery");
const zustandStore_1 = require("../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../atoms/button/simpleButton"));
const footer_1 = __importDefault(require("../../organisms/footer"));
const lexico = new lexico_1.default();
const MAX_MESSAGE_LENGTH = 300;
const StyledTextField = (0, material_1.styled)(material_1.TextField)(({ theme }) => ({
    backgroundColor: theme.palette.tsGoColor.white,
    paddingRight: theme.tsGoUnit.tsUnitHalf,
    height: '100%',
    boxSizing: 'border-box',
    '& .MuiInput-underline': {
        fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
        fontWeight: theme.typography.tsFontWeight.default,
        height: 'inherit',
    },
    '& .MuiInput-underline:before': {
        borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
        borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
        borderBottom: 'none',
    },
}));
const RequestsFooter = () => {
    const initialData = (0, react_router_1.useLoaderData)();
    const { data: currentUser } = (0, react_query_1.useQuery)({
        ...userQuery_1.userQuery,
        initialData: initialData.user,
    });
    const viewApprovalConversationRequestId = (0, zustandStore_1.useZustand)(state => state.approvals.viewApprovalConversationRequestId);
    const conversationApproval = initialData.conversationApproval;
    const { requestId = conversationApproval?.request?.requestId || viewApprovalConversationRequestId || '' } = (0, react_router_1.useParams)();
    const [message, setMessage] = (0, react_1.useState)('');
    const { mutate: addMessage, isPending } = (0, conversationQuery_1.useAddMessageMutation)();
    const { setDialog, closeDialog } = (0, zustandStore_1.useZustand)();
    const inputRef = (0, react_1.useRef)(null);
    const allEvents = (0, zustandStore_1.useZustand)(state => state.request.allEvents);
    const { setAllEvents, setFooterButtons } = (0, zustandStore_1.useZustand)();
    const handleSendMessage = (0, react_1.useCallback)(() => {
        if (!requestId || !message.trim()) {
            return;
        }
        if (message.length > MAX_MESSAGE_LENGTH) {
            setDialog({
                message: lexico.trc('Warning message', `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters.`),
                type: strings_1.notificationStrings.WARNING,
                open: true,
                actionText: lexico.trc('Button', 'OK'),
                showCancel: false,
                onAccept: closeDialog,
            });
            return;
        }
        addMessage({ conversationId: requestId, message, currentUser }, {
            onSuccess: () => {
                setMessage('');
            },
            onError: () => {
                setDialog({
                    message: lexico.trc('Warning message', 'Could not send your message, please try again.'),
                    type: strings_1.notificationStrings.WARNING,
                    open: true,
                    actionText: lexico.trc('Button', 'OK'),
                    showCancel: false,
                    onAccept: closeDialog,
                });
            },
        });
        const typedArray = new Uint8Array(1);
        const newEvents = [
            ...allEvents,
            {
                author: currentUser,
                data: {
                    body: message,
                },
                messageId: crypto.getRandomValues(typedArray)[0] || 1,
                sequenceNumber: crypto.getRandomValues(typedArray)[0] || 1,
                requestId,
                timestamp: Date.now(),
                type: 'messageAdded',
            },
        ];
        setAllEvents(newEvents);
    }, [requestId, message, allEvents, currentUser]);
    const handleInputChange = (event) => {
        const newValue = event.target.value;
        if (newValue.length <= MAX_MESSAGE_LENGTH) {
            setMessage(newValue);
        }
    };
    (0, react_1.useEffect)(() => {
        if (!isPending && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isPending]);
    (0, react_1.useEffect)(() => {
        setFooterButtons([]);
        return setFooterButtons([]);
    }, []);
    return ((0, jsx_runtime_1.jsxs)(footer_1.default, { children: [(0, jsx_runtime_1.jsx)(StyledTextField, { id: "outlined-multiline-static", autoFocus: true, inputRef: inputRef, rows: 1, value: message, onChange: handleInputChange, onKeyDown: (event) => {
                    if (event.key === 'Enter') {
                        handleSendMessage();
                    }
                }, placeholder: lexico.trc('Placeholder text', `Say something... (Max ${MAX_MESSAGE_LENGTH} characters)`), disabled: isPending, variant: "standard", helperText: `${message.length}/${MAX_MESSAGE_LENGTH} characters`, error: message.length > MAX_MESSAGE_LENGTH, slotProps: {
                    htmlInput: {
                        maxLength: MAX_MESSAGE_LENGTH,
                    },
                } }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: 'sendMessage-btn', label: lexico.trc('Button', 'Send message'), disabled: !message.trim() || isPending, onClick: handleSendMessage, color: uiConstants_1.UI_BUTTON_PRIMARY, style: { margin: 2 } }, 'Send message')] }));
};
exports.default = RequestsFooter;
